import { useEffect, useState } from 'react';
import OneliveFeaturedSlider from './OneliveFeaturedSlider';
import OnelivePageLayout from './OnelivePageLayout';
import OneliveTopBanner from './OneliveTopBanner';
import {
  LiveRoom,
  ONELIVE_SEARCH_CHANGED,
  fetchLiveRooms,
  getSearchQueryFromUrl,
  navigate,
} from './api';
import { handleLiveRoomClick } from './liveEntryGuard';
import { useLiveUiMessages } from './useLiveUiMessages';

function shuffleLiveRooms<T>(items: T[]): T[] {
  const next = [...items];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
}

function resolveViewerCount(room: LiveRoom): number {
  const min = room.viewer_count_min ?? null;
  const max = room.viewer_count_max ?? null;
  if (min !== null && max !== null) {
    const low = Math.min(min, max);
    const high = Math.max(min, max);
    return low + Math.floor(Math.random() * (high - low + 1));
  }
  return room.viewer_count ?? 0;
}

function decorateLiveRooms(items: LiveRoom[]): LiveRoom[] {
  return shuffleLiveRooms(items).map((room) => ({
    ...room,
    viewer_count: resolveViewerCount(room),
  }));
}

interface LiveHomePageProps {
  search?: string;
}

export default function LiveHomePage({ search: searchProp }: LiveHomePageProps) {
  const [rooms, setRooms] = useState<LiveRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const liveUiMessages = useLiveUiMessages();

  useEffect(() => {
    const syncSearch = () => {
      const q = (searchProp ?? getSearchQueryFromUrl()).trim();
      setSearchQuery(q);
    };
    syncSearch();
    window.addEventListener('popstate', syncSearch);
    window.addEventListener(ONELIVE_SEARCH_CHANGED, syncSearch as EventListener);
    return () => {
      window.removeEventListener('popstate', syncSearch);
      window.removeEventListener(ONELIVE_SEARCH_CHANGED, syncSearch as EventListener);
    };
  }, [searchProp]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const r = await fetchLiveRooms(200, searchQuery || undefined);
        if (mounted) {
          setRooms(decorateLiveRooms(r));
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [searchQuery]);

  useEffect(() => {
    if (searchQuery) return undefined;

    const timer = window.setInterval(() => {
      setRooms((current) => decorateLiveRooms(current));
    }, 45000);

    return () => window.clearInterval(timer);
  }, [searchQuery]);

  const clearSearch = () => navigate('/');

  const onLiveCardClick = (room: LiveRoom) => {
    handleLiveRoomClick(room, liveUiMessages, searchQuery ? `/live?search=${encodeURIComponent(searchQuery)}` : '/live');
  };

  return (
    <OnelivePageLayout pageClassName="onelive-home-page" sidebarRooms={rooms} showInlineFooter={false}>
      {!searchQuery && <OneliveTopBanner rooms={rooms} />}
      {!searchQuery && <OneliveFeaturedSlider rooms={rooms} />}

      <section className="onelive-section">
        <div className="onelive-section-head">
          <h2 className="onelive-section-title">
            {searchQuery ? `"${searchQuery}" 검색 결과` : 'LIVE NOW'}
          </h2>
          {searchQuery ? (
            <button type="button" className="onelive-search-clear" onClick={clearSearch}>
              검색 초기화
            </button>
          ) : null}
        </div>
        {loading ? (
          <p className="onelive-loading">로딩 중...</p>
        ) : rooms.length === 0 ? (
          <p className="onelive-loading">
            {searchQuery ? '검색 결과가 없습니다.' : '표시할 라이브가 없습니다.'}
          </p>
        ) : (
          <div className="onelive-live-grid">
            {rooms.map((room) => (
              <article
                key={room.uuid}
                className="onelive-live-card"
                onClick={() => onLiveCardClick(room)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onLiveCardClick(room)}
              >
                <div className="onelive-live-thumb">
                  {room.thumbnail ? (
                    <img src={room.thumbnail} alt="" />
                  ) : (
                    <span className="onelive-thumb-placeholder">{room.bj_nickname?.[0]}</span>
                  )}
                  <ul className="onelive-live-badges">
                    <li className="onelive-live-badge">LIVE</li>
                    <li className="onelive-viewer-count">{room.viewer_count ?? 0}명</li>
                  </ul>
                </div>
                <div className="onelive-live-info">
                  <div className="onelive-live-meta">
                    <span className="onelive-live-avatar">
                      {room.avatar ? (
                        <img src={room.avatar} alt="" />
                      ) : (
                        room.bj_nickname?.[0]
                      )}
                    </span>
                    <div className="onelive-live-text">
                      <strong className="onelive-room-title">{room.title}</strong>
                      {room.bj_nickname && (
                        <span className="onelive-bj-name">{room.bj_nickname}</span>
                      )}
                      {(room.tags?.length ? room.tags : room.category ? [room.category] : []).map((tag) => (
                        <span key={tag} className="onelive-category-tag">{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </OnelivePageLayout>
  );
}
